import { supabase } from '../lib/supabase.js';
import { OrdersManager } from './OrdersManager.js';
import { CommunicationTemplateManager } from './CommunicationTemplateManager.js';
import { CommunicationTester } from './CommunicationTester.js';
import { DatabaseManager } from './DatabaseManager.js';
import { TriggerDiagnostics } from './TriggerDiagnostics.js';
import { ClothingItemsManager } from './ClothingItemsManager.js';
import { ChatManager } from './ChatManager.js';
import { CouponManager } from './CouponManager.js';
import { SmsManager } from './SmsManager.js';
import { ReferralManager } from './ReferralManager.js';
import { ManualOrderManager } from './ManualOrderManager.js';

export class DashboardManager {
  constructor() {
    this.currentView = 'overview';
    this.currentUser = null;
    
    // Initialize managers
    this.ordersManager = new OrdersManager();
    this.communicationTemplateManager = new CommunicationTemplateManager();
    this.communicationTester = new CommunicationTester();
    this.databaseManager = new DatabaseManager();
    this.triggerDiagnostics = new TriggerDiagnostics();
    this.clothingItemsManager = new ClothingItemsManager();
    this.chatManager = new ChatManager();
    this.couponManager = new CouponManager();
    this.smsManager = new SmsManager();
    this.referralManager = new ReferralManager();
    this.manualOrderManager = new ManualOrderManager();
    this.hasNewSms = false; // New state for SMS notification
  }

  render(user) {
    this.currentUser = user;
    
    return `
      <div class="min-h-screen bg-gray-50">
        <!-- Navigation Header -->
        <nav class="bg-white shadow-sm border-b border-gray-200">
          <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div class="flex justify-between h-16">
              <div class="flex items-center">
                <h1 class="text-xl font-semibold text-gray-900">Daily Ironing Dashboard</h1>
              </div>
              
              <div class="flex items-center space-x-4">
                <span class="text-sm text-gray-600">Welcome, ${user?.email || 'Admin'}</span>
                <button 
                  onclick="dashboardManager.signOut()" 
                  class="text-sm text-gray-500 hover:text-gray-700"
                >
                  Sign Out
                </button>
              </div>
            </div>
          </div>
        </nav>

        <div class="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          <!-- Tab Navigation -->
          <div class="mb-6">
            <nav class="flex space-x-8" aria-label="Tabs">
              <button 
                onclick="dashboardManager.switchView('overview')" 
                class="tab-button ${this.currentView === 'overview' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm"
              >
                Overview
              </button>
              <button 
                onclick="dashboardManager.switchView('orders')" 
                class="tab-button ${this.currentView === 'orders' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm"
              >
                Orders
              </button>
              <button 
                onclick="dashboardManager.switchView('manual-order')" 
                class="tab-button ${this.currentView === 'manual-order' ? 'border-violet-500 text-violet-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm"
              >
                Manual Order
              </button>
              <button 
                onclick="dashboardManager.switchView('clothing-items')" 
                class="tab-button ${this.currentView === 'clothing-items' ? 'border-violet-500 text-violet-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm"
              >
                Clothing Items
              </button>
              <button 
                onclick="dashboardManager.switchView('coupons')" 
                class="tab-button ${this.currentView === 'coupons' ? 'border-violet-500 text-violet-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm"
              >
                Coupons
              </button>
              <button 
                onclick="dashboardManager.switchView('chat')" 
                class="tab-button ${this.currentView === 'chat' ? 'border-violet-500 text-violet-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm"
              >
                Chat
              </button>
              <button 
                onclick="dashboardManager.switchView('sms')" 
                class="tab-button relative ${this.currentView === 'sms' ? 'border-violet-500 text-violet-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm"
              >
                SMS
                ${this.hasNewSms ? '<span class="absolute top-0 right-0 -mt-1 -mr-1 h-2 w-2 rounded-full bg-red-500"></span>' : ''}
              </button>
              <button 
                onclick="dashboardManager.switchView('referrals')" 
                class="tab-button ${this.currentView === 'referrals' ? 'border-violet-500 text-violet-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm"
              >
                Referrals
              </button>
              <button 
                onclick="dashboardManager.switchView('templates')" 
                class="tab-button ${this.currentView === 'templates' ? 'border-violet-500 text-violet-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm"
              >
                Communication Templates
              </button>
              <button 
                onclick="dashboardManager.switchView('tester')" 
                class="tab-button ${this.currentView === 'tester' ? 'border-violet-500 text-violet-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm"
              >
                Communication Tester
              </button>
              <button 
                onclick="dashboardManager.switchView('database')" 
                class="tab-button ${this.currentView === 'database' ? 'border-violet-500 text-violet-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm"
              >
                Database
              </button>
              <button 
                onclick="dashboardManager.switchView('diagnostics')" 
                class="tab-button ${this.currentView === 'diagnostics' ? 'border-violet-500 text-violet-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm"
              >
                Diagnostics
              </button>
              <button 
                onclick="dashboardManager.testPabblyWebhook()" 
                class="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 ml-4"
              >
                Test Pabbly Webhook
              </button>
            </nav>
          </div>

          <!-- Content Area -->
          <div id="dashboard-content">
            ${this.renderCurrentView()}
          </div>
        </div>

        <!-- Webhook Test Results Modal -->
        <div id="webhook-test-modal" class="hidden fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div class="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
            <div class="mt-3">
              <div class="flex justify-between items-center mb-4">
                <h3 class="text-lg font-medium text-gray-900">Pabbly Webhook Test Results</h3>
                <button 
                  id="close-webhook-modal-btn"
                  class="text-gray-400 hover:text-gray-600"
                >
                  <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </button>
              </div>

              <div id="webhook-test-content" class="space-y-4">
                <!-- Test results will be populated here -->
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  renderCurrentView() {
    switch (this.currentView) {
      case 'overview':
        return this.renderOverview();
      case 'orders':
        return this.ordersManager.render();
      case 'manual-order':
        return this.manualOrderManager.render();
      case 'clothing-items':
        return this.clothingItemsManager.render();
      case 'coupons':
        return this.couponManager.render();
      case 'sms':
        return this.smsManager.render();
      case 'referrals':
        return this.referralManager.render();
      case 'chat':
        return this.chatManager.render();
      case 'templates':
        return this.communicationTemplateManager.render();
      case 'tester':
        return this.communicationTester.render();
      case 'database':
        return this.databaseManager.render();
      case 'diagnostics':
        return this.triggerDiagnostics.render();
      default:
        return this.renderOverview();
    }
  }

  renderOverview() {
    return `
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
        <!-- Quick Stats -->
        <div class="bg-white overflow-hidden shadow rounded-lg">
          <div class="p-5">
            <div class="flex items-center">
              <div class="flex-shrink-0">
                <div class="w-8 h-8 bg-indigo-500 rounded-md flex items-center justify-center">
                  <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                  </svg>
                </div>
              </div>
              <div class="ml-5 w-0 flex-1">
                <dl>
                  <dt class="text-sm font-medium text-gray-500 truncate">Total Orders</dt>
                  <dd class="text-lg font-medium text-gray-900" id="total-orders">Loading...</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div class="bg-white overflow-hidden shadow rounded-lg">
          <div class="p-5">
            <div class="flex items-center">
              <div class="flex-shrink-0">
                <div class="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                  <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
                  </svg>
                </div>
              </div>
              <div class="ml-5 w-0 flex-1">
                <dl>
                  <dt class="text-sm font-medium text-gray-500 truncate">Total Customers</dt>
                  <dd class="text-lg font-medium text-gray-900" id="total-customers">Loading...</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div class="bg-white overflow-hidden shadow rounded-lg">
          <div class="p-5">
            <div class="flex items-center">
              <div class="flex-shrink-0">
                <div class="w-8 h-8 bg-yellow-500 rounded-md flex items-center justify-center">
                  <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"></path>
                  </svg>
                </div>
              </div>
              <div class="ml-5 w-0 flex-1">
                <dl>
                  <dt class="text-sm font-medium text-gray-500 truncate">Communications Sent</dt>
                  <dd class="text-lg font-medium text-gray-900" id="total-communications">Loading...</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div class="bg-white overflow-hidden shadow rounded-lg">
          <div class="p-5">
            <div class="flex items-center">
              <div class="flex-shrink-0">
                <div class="w-8 h-8 bg-green-600 rounded-md flex items-center justify-center">
                  <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"></path>
                  </svg>
                </div>
              </div>
              <div class="ml-5 w-0 flex-1">
                <dl>
                  <dt class="text-sm font-medium text-gray-500 truncate">Total Revenue</dt>
                  <dd class="text-lg font-medium text-gray-900" id="total-revenue">Loading...</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div class="bg-white overflow-hidden shadow rounded-lg">
          <div class="p-5">
            <div class="flex items-center">
              <div class="flex-shrink-0">
                <div class="w-8 h-8 bg-blue-600 rounded-md flex items-center justify-center">
                  <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
                  </svg>
                </div>
              </div>
              <div class="ml-5 w-0 flex-1">
                <dl>
                  <dt class="text-sm font-medium text-gray-500 truncate">Net Revenue</dt>
                  <dd class="text-lg font-medium text-gray-900" id="net-revenue">Loading...</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div class="bg-white overflow-hidden shadow rounded-lg">
          <div class="p-5">
            <div class="flex items-center">
              <div class="flex-shrink-0">
                <div class="w-8 h-8 bg-red-500 rounded-md flex items-center justify-center">
                  <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"></path>
                  </svg>
                </div>
              </div>
              <div class="ml-5 w-0 flex-1">
                <dl>
                  <dt class="text-sm font-medium text-gray-500 truncate">Total Discount Given</dt>
                  <dd class="text-lg font-medium text-gray-900" id="total-discount-given">Loading...</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Charts Section -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <!-- Sales Chart -->
        <div class="bg-white shadow rounded-lg p-6">
          <div class="flex justify-between items-center mb-4">
            <h3 class="text-lg font-medium text-gray-900">Sales Trends</h3>
            <select id="sales-period-filter" class="text-sm border border-gray-300 rounded px-2 py-1">
              <option value="7">Last 7 Days</option>
              <option value="30" selected>Last 30 Days</option>
              <option value="90">Last 90 Days</option>
            </select>
          </div>
          <div class="h-80">
            <canvas id="sales-chart"></canvas>
          </div>
        </div>

        <!-- Order Status Chart -->
        <div class="bg-white shadow rounded-lg p-6">
          <h3 class="text-lg font-medium text-gray-900 mb-4">Order Status Distribution</h3>
          <div class="h-80">
            <canvas id="order-status-chart"></canvas>
          </div>
        </div>
      </div>

      <!-- Additional Analytics -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <!-- Revenue by Service Area -->
        <div class="bg-white shadow rounded-lg p-6">
          <h3 class="text-lg font-medium text-gray-900 mb-4">Revenue by Service Area</h3>
          <div class="h-64">
            <canvas id="service-area-chart"></canvas>
          </div>
        </div>

        <!-- Monthly Growth -->
        <div class="bg-white shadow rounded-lg p-6">
          <h3 class="text-lg font-medium text-gray-900 mb-4">Monthly Growth</h3>
          <div class="h-64">
            <canvas id="monthly-growth-chart"></canvas>
          </div>
        </div>
      </div>

      <!-- Recent Activity -->
      <div class="mt-8">
        <div class="bg-white shadow rounded-lg">
          <div class="px-4 py-5 sm:p-6">
            <h3 class="text-lg leading-6 font-medium text-gray-900 mb-4">Recent Activity</h3>
            <div id="recent-activity">
              <div class="text-gray-500">Loading recent activity...</div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  async bindEvents() {
    // Bind events for the current view
    await this.bindCurrentViewEvents();
  }

  async bindCurrentViewEvents() {
    switch (this.currentView) {
      case 'overview':
        await this.bindOverviewEvents();
        break;
      case 'orders':
        await this.ordersManager.bindEvents();
        break;
      case 'manual-order':
        await this.manualOrderManager.bindEvents();
        break;
      case 'clothing-items':
        await this.clothingItemsManager.bindEvents();
        break;
      case 'coupons':
        await this.couponManager.bindEvents();
        break;
      case 'chat':
        await this.chatManager.bindEvents();
        break;
      case 'sms':
        await this.smsManager.bindEvents();
        break;
      case 'referrals':
        await this.referralManager.bindEvents();
        break;
      case 'templates':
        await this.communicationTemplateManager.bindEvents();
        break;
      case 'tester':
        await this.communicationTester.bindEvents();
        break;
      case 'database':
        await this.databaseManager.bindEvents();
        break;
      case 'diagnostics':
        await this.triggerDiagnostics.bindEvents();
        break;
    }
  }

  async bindOverviewEvents() {
    // Load overview data
    setTimeout(async () => {
      await this.loadOverviewData();
    }, 0);
    
    // Bind chart filter events
    const salesPeriodFilter = document.getElementById('sales-period-filter');
    if (salesPeriodFilter) {
      salesPeriodFilter.addEventListener('change', async (e) => {
        const period = parseInt(e.target.value);
        await this.loadSalesData(period);
        this.renderSalesChart();
      });
    }
  }

  async loadOverviewData() {
    try {
      // Load basic stats
      // Load counts
      const [orders, customers, communications, revenueData, stripeTransactionData] = await Promise.all([
        supabase.from('orders').select('*', { count: 'exact', head: true }),
        supabase.from('customers').select('*', { count: 'exact', head: true }),
        supabase.from('communication_history').select('*', { count: 'exact', head: true }),
        supabase.from('orders').select('total_amount, discount_amount').eq('payment_status', 'succeeded'),
        supabase.from('orders').select('total_amount').eq('payment_status', 'succeeded')
      ]);

      document.getElementById('total-orders').textContent = orders.count || 0;
      document.getElementById('total-customers').textContent = customers.count || 0;
      document.getElementById('total-communications').textContent = communications.count || 0;
      
      // Calculate total revenue (what was actually received) and total discount given
      const totalRevenue = revenueData.data?.reduce((sum, order) => sum + parseFloat(order.total_amount || 0), 0) || 0;
      const totalDiscountGiven = revenueData.data?.reduce((sum, order) => sum + parseFloat(order.discount_amount || 0), 0) || 0;
      
      // Calculate Stripe fees (2.9% + $0.30 per transaction)
      const totalStripeFees = stripeTransactionData.data?.reduce((sum, order) => {
        const orderAmount = parseFloat(order.total_amount || 0);
        const stripeFee = (orderAmount * 0.029) + 0.30; // 2.9% + $0.30
        return sum + stripeFee;
      }, 0) || 0;
      
      // Calculate net revenue (total revenue - Stripe fees)
      const netRevenue = totalRevenue - totalStripeFees;
      
      document.getElementById('total-revenue').textContent = `$${totalRevenue.toFixed(2)}`;
      document.getElementById('net-revenue').textContent = `$${netRevenue.toFixed(2)}`;
      document.getElementById('total-discount-given').textContent = `$${totalDiscountGiven.toFixed(2)}`;

      // Load recent activity
      await this.loadRecentActivity();
      
      // Load and render charts
      await this.loadSalesData(30); // Default to 30 days
      await this.loadOrderStatusBreakdown();
      await this.loadServiceAreaData();
      await this.loadMonthlyGrowthData();
      
      this.renderSalesChart();
      this.renderOrderStatusChart();
      this.renderServiceAreaChart();
      this.renderMonthlyGrowthChart();
    } catch (error) {
      console.error('Error loading overview data:', error);
    }
  }

  async loadRecentActivity() {
    try {
      const { data: recentOrders, error } = await supabase
        .from('orders')
        .select(`
          order_number,
          status,
          created_at,
          customers (full_name)
        `)
        .order('created_at', { ascending: false })
        .limit(5);

      if (error) throw error;

      const activityDiv = document.getElementById('recent-activity');
      
      if (!activityDiv) {
        console.warn('recent-activity element not found in DOM');
        return;
      }

      if (recentOrders.length === 0) {
        activityDiv.innerHTML = '<div class="text-gray-500">No recent activity</div>';
        return;
      }

      activityDiv.innerHTML = recentOrders.map(order => `
        <div class="flex items-center justify-between py-2 border-b border-gray-200 last:border-b-0">
          <div>
            <div class="text-sm font-medium text-gray-900">
              Order ${order.order_number}
            </div>
            <div class="text-sm text-gray-500">
              ${order.customers?.full_name || 'Unknown Customer'} • ${order.status}
            </div>
          </div>
          <div class="text-sm text-gray-500">
            ${new Date(order.created_at).toLocaleDateString()}
          </div>
        </div>
      `).join('');
    } catch (error) {
      const activityDiv = document.getElementById('recent-activity');
      if (activityDiv) {
        activityDiv.innerHTML = '<div class="text-red-500">Error loading recent activity</div>';
      } else {
        console.warn('recent-activity element not found in DOM during error handling');
      }
    }
  }

  async loadSalesData(days = 30) {
    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);
      
      const { data: salesData, error } = await supabase
        .from('orders')
        .select('created_at, total_amount')
        .eq('payment_status', 'succeeded')
        .gte('created_at', startDate.toISOString())
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Group sales by date
      const salesByDate = {};
      const currentDate = new Date(startDate);
      
      // Initialize all dates with 0
      while (currentDate <= new Date()) {
        const dateKey = currentDate.toISOString().split('T')[0];
        salesByDate[dateKey] = 0;
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      // Aggregate actual sales
      salesData.forEach(order => {
        const dateKey = order.created_at.split('T')[0];
        salesByDate[dateKey] = (salesByDate[dateKey] || 0) + parseFloat(order.total_amount);
      });

      this.salesData = {
        labels: Object.keys(salesByDate).map(date => new Date(date).toLocaleDateString()),
        data: Object.values(salesByDate)
      };
    } catch (error) {
      console.error('Error loading sales data:', error);
      this.salesData = { labels: [], data: [] };
    }
  }

  async loadOrderStatusBreakdown() {
    try {
      const { data: statusData, error } = await supabase
        .from('orders')
        .select('status')
        .order('status');

      if (error) throw error;

      const statusCounts = {};
      statusData.forEach(order => {
        statusCounts[order.status] = (statusCounts[order.status] || 0) + 1;
      });

      // Format status names for display
      const formatStatus = (status) => {
        return status.split('_').map(word => 
          word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ');
      };

      this.orderStatusData = {
        labels: Object.keys(statusCounts).map(formatStatus),
        data: Object.values(statusCounts),
        colors: [
          '#8B5CF6', '#10B981', '#F59E0B', '#EF4444', '#6366F1',
          '#06B6D4', '#84CC16', '#F97316', '#EC4899', '#6B7280'
        ]
      };
    } catch (error) {
      console.error('Error loading order status data:', error);
      this.orderStatusData = { labels: [], data: [], colors: [] };
    }
  }

  async loadServiceAreaData() {
    try {
      const { data: areaData, error } = await supabase
        .from('orders')
        .select(`
          total_amount,
          customers (service_area)
        `)
        .eq('payment_status', 'succeeded');

      if (error) throw error;

      const revenueByArea = {};
      areaData.forEach(order => {
        const area = order.customers?.service_area || 'Unknown';
        revenueByArea[area] = (revenueByArea[area] || 0) + parseFloat(order.total_amount);
      });

      // Format area names
      const formatArea = (area) => {
        return area.split('-').map(word => 
          word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ');
      };

      this.serviceAreaData = {
        labels: Object.keys(revenueByArea).map(formatArea),
        data: Object.values(revenueByArea),
        colors: ['#8B5CF6', '#10B981', '#F59E0B']
      };
    } catch (error) {
      console.error('Error loading service area data:', error);
      this.serviceAreaData = { labels: [], data: [], colors: [] };
    }
  }

  async loadMonthlyGrowthData() {
    try {
      const { data: monthlyData, error } = await supabase
        .from('orders')
        .select('created_at, total_amount')
        .eq('payment_status', 'succeeded')
        .gte('created_at', new Date(new Date().getFullYear(), 0, 1).toISOString())
        .order('created_at', { ascending: true });

      if (error) throw error;

      const monthlyRevenue = {};
      const monthNames = [
        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
      ];

      // Initialize all months with 0
      monthNames.forEach((month, index) => {
        monthlyRevenue[month] = 0;
      });

      // Aggregate monthly revenue
      monthlyData.forEach(order => {
        const month = new Date(order.created_at).getMonth();
        const monthName = monthNames[month];
        monthlyRevenue[monthName] += parseFloat(order.total_amount);
      });

      this.monthlyGrowthData = {
        labels: monthNames,
        data: Object.values(monthlyRevenue)
      };
    } catch (error) {
      console.error('Error loading monthly growth data:', error);
      this.monthlyGrowthData = { labels: [], data: [] };
    }
  }

  renderSalesChart() {
    const ctx = document.getElementById('sales-chart');
    if (!ctx || !this.salesData) return;

    // Destroy existing chart if it exists
    if (this.salesChart) {
      this.salesChart.destroy();
    }

    this.salesChart = new Chart(ctx, {
      type: 'line',
      data: {
        labels: this.salesData.labels,
        datasets: [{
          label: 'Daily Revenue ($)',
          data: this.salesData.data,
          borderColor: '#8B5CF6',
          backgroundColor: 'rgba(139, 92, 246, 0.1)',
          borderWidth: 2,
          fill: true,
          tension: 0.4
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              callback: function(value) {
                return '$' + value.toFixed(0);
              }
            }
          },
          x: {
            ticks: {
              maxTicksLimit: 10
            }
          }
        }
      }
    });
  }

  renderOrderStatusChart() {
    const ctx = document.getElementById('order-status-chart');
    if (!ctx || !this.orderStatusData) return;

    // Destroy existing chart if it exists
    if (this.orderStatusChart) {
      this.orderStatusChart.destroy();
    }

    this.orderStatusChart = new Chart(ctx, {
      type: 'doughnut',
      data: {
        labels: this.orderStatusData.labels,
        datasets: [{
          data: this.orderStatusData.data,
          backgroundColor: this.orderStatusData.colors,
          borderWidth: 2,
          borderColor: '#ffffff'
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'bottom',
            labels: {
              padding: 20,
              usePointStyle: true
            }
          }
        }
      }
    });
  }

  renderServiceAreaChart() {
    const ctx = document.getElementById('service-area-chart');
    if (!ctx || !this.serviceAreaData) return;

    // Destroy existing chart if it exists
    if (this.serviceAreaChart) {
      this.serviceAreaChart.destroy();
    }

    this.serviceAreaChart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: this.serviceAreaData.labels,
        datasets: [{
          label: 'Revenue ($)',
          data: this.serviceAreaData.data,
          backgroundColor: this.serviceAreaData.colors,
          borderWidth: 1,
          borderColor: '#ffffff'
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              callback: function(value) {
                return '$' + value.toFixed(0);
              }
            }
          }
        }
      }
    });
  }

  renderMonthlyGrowthChart() {
    const ctx = document.getElementById('monthly-growth-chart');
    if (!ctx || !this.monthlyGrowthData) return;

    // Destroy existing chart if it exists
    if (this.monthlyGrowthChart) {
      this.monthlyGrowthChart.destroy();
    }

    this.monthlyGrowthChart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: this.monthlyGrowthData.labels,
        datasets: [{
          label: 'Monthly Revenue ($)',
          data: this.monthlyGrowthData.data,
          backgroundColor: '#8B5CF6',
          borderWidth: 1,
          borderColor: '#ffffff'
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              callback: function(value) {
                return '$' + value.toFixed(0);
              }
            }
          }
        }
      }
    });
  }

  async switchView(view) {
    // Cleanup previous view if it has a cleanup method
    if (this.currentView === 'chat' && this.chatManager.cleanup) {
      this.chatManager.cleanup();
    }
    if (this.currentView === 'sms' && this.smsManager.cleanup) {
      this.smsManager.cleanup();
    }
    if (this.currentView === 'manual-order' && this.manualOrderManager.cleanup) {
      this.manualOrderManager.cleanup();
    }
    
    // Clear SMS notification if switching to SMS tab
    if (view === 'sms') {
      this.hasNewSms = false;
    }

    this.currentView = view;
    
    // Re-render the entire dashboard to update the tab styles and potentially clear the SMS indicator
    const app = document.getElementById('app');
    if (app) {
      app.innerHTML = this.render(this.currentUser);
      setTimeout(async () => {
        await this.bindEvents(); // Re-bind all events for the new view
      }, 0);
    }
  }

  notifyNewSmsReceived() {
    if (this.currentView !== 'sms') { // Only show indicator if not currently on SMS tab
      this.hasNewSms = true;
      // Re-render the entire dashboard to update the tab indicator
      const app = document.getElementById('app');
      if (app) {
        app.innerHTML = this.render(this.currentUser);
        this.bindEvents(); // Re-bind all events
      }
    }
  }

  async signOut() {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    } catch (error) {
      console.error('Error signing out:', error);
      alert('Error signing out: ' + error.message);
    }
  }

  async testPabblyWebhook() {
    try {
      // Show loading state
      this.showWebhookTestModal('Testing Pabbly webhook...', 'loading');
      
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/test-pabbly-webhook`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({})
      });
      
      const result = await response.json();

      if (response.ok && result.success) {
        this.showWebhookTestModal('✅ Webhook test successful!', 'success', result);
      } else {
        this.showWebhookTestModal('❌ Webhook test failed', 'error', result);
      }
    } catch (error) {
      this.showWebhookTestModal('❌ Error testing webhook', 'error', { error: error.message });
    }
  }

  showWebhookTestModal(message, type, data = null) {
    const modal = document.getElementById('webhook-test-modal');
    const content = document.getElementById('webhook-test-content');
    
    if (!modal || !content) return;
    
    let colorClass = '';
    switch (type) {
      case 'loading':
        colorClass = 'text-blue-600';
        break;
      case 'success':
        colorClass = 'text-green-600';
        break;
      case 'error':
        colorClass = 'text-red-600';
        break;
      default:
        colorClass = 'text-gray-600';
    }
    
    let contentHtml = `<div class="${colorClass} text-lg font-medium mb-4">${message}</div>`;
    
    if (type === 'loading') {
      contentHtml += `
        <div class="flex items-center justify-center py-8">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      `;
    } else if (data) {
      contentHtml += `
        <div class="space-y-4">
          ${data.webhook_url ? `
            <div class="bg-gray-50 p-3 rounded-md">
              <strong>Webhook URL:</strong><br>
              <code class="text-sm">${data.webhook_url}</code>
            </div>
          ` : ''}
          
          ${data.response_status ? `
            <div class="bg-gray-50 p-3 rounded-md">
              <strong>Response Status:</strong> ${data.response_status}
            </div>
          ` : ''}
          
          ${data.error ? `
            <div class="bg-red-50 p-3 rounded-md border border-red-200">
              <strong class="text-red-800">Error:</strong><br>
              <code class="text-sm text-red-700">${data.error}</code>
            </div>
          ` : ''}
          
          ${data.response_body ? `
            <div class="bg-gray-50 p-3 rounded-md">
              <strong>Pabbly Response:</strong><br>
              <pre class="text-sm text-gray-700 whitespace-pre-wrap">${data.response_body}</pre>
            </div>
          ` : ''}
          
          ${data.test_data ? `
            <div class="bg-blue-50 p-3 rounded-md border border-blue-200">
              <strong class="text-blue-800">Sample Data Sent:</strong><br>
              <details class="mt-2">
                <summary class="cursor-pointer text-blue-700 hover:text-blue-800">View JSON Payload</summary>
                <pre class="text-xs text-blue-700 mt-2 whitespace-pre-wrap overflow-x-auto">${JSON.stringify(data.test_data, null, 2)}</pre>
              </details>
            </div>
          ` : ''}
        </div>
      `;
    }
    
    content.innerHTML = contentHtml;
    modal.classList.remove('hidden');
    
    // Bind close button event
    const closeBtn = document.getElementById('close-webhook-modal-btn');
    if (closeBtn) {
      closeBtn.onclick = () => {
        modal.classList.add('hidden');
      };
    }
    
    // Close modal when clicking outside
    modal.onclick = (e) => {
      if (e.target.id === 'webhook-test-modal') {
        modal.classList.add('hidden');
      }
    };
  }
}